import { CheckCircle, type IconComponent, InfoOutline, Warning } from "@gigpro/ui/Icons";
import type { NormalizedInterviewTypeEnum } from "@sidegigllc/api/client";

export const INTERVIEW_TYPE_LABEL: Record<NormalizedInterviewTypeEnum, string> = {
  working: "Working Interview",
  phone: "Phone Interview",
  video: "Video Interview",
  in_person: "In-Person Interview",
};

type StatusColor = "info" | "success" | "brand" | "warning" | "error" | "medium-gray" | "gray";

// Kept literal (no `bg-${color}`) so Tailwind can see every class.
const STATUS_STYLES: Record<StatusColor, { bg: string; text: string; card: string; chip: string; accent: string }> = {
  info: {
    bg: "bg-info",
    text: "text-white",
    card: "border-l-info bg-info/10",
    chip: "bg-accent-light-blue",
    accent: "text-info",
  },
  success: {
    bg: "bg-success",
    text: "text-white",
    card: "border-l-success bg-success/10",
    chip: "bg-accent-light-green",
    accent: "text-success",
  },
  brand: {
    bg: "bg-brand",
    text: "text-white",
    card: "border-l-brand bg-brand/10",
    chip: "bg-brand/10",
    accent: "text-brand",
  },
  warning: {
    bg: "bg-warning",
    text: "text-black",
    card: "border-l-warning bg-warning/10",
    chip: "bg-accent-light-orange",
    accent: "text-warning",
  },
  error: {
    bg: "bg-error",
    text: "text-white",
    card: "border-l-error bg-error/10",
    chip: "bg-accent-light-red",
    accent: "text-error",
  },
  "medium-gray": {
    bg: "bg-medium-gray",
    text: "text-white",
    card: "border-l-medium-gray bg-medium-gray/10",
    chip: "bg-medium-gray/10",
    accent: "text-medium-gray",
  },
  gray: {
    bg: "bg-gray",
    text: "text-white",
    card: "border-l-gray bg-gray/10",
    chip: "bg-gray/10",
    accent: "text-gray",
  },
};

type StatusBucket = {
  id: string;
  name: string;
  color: StatusColor;
  icon: IconComponent;
  control: string;
  members: ReadonlyArray<string>;
};

const STATUS_BUCKETS = [
  {
    id: "invited",
    name: "Invited",
    color: "info",
    icon: InfoOutline,
    control: "data-[state=checked]:border-info data-[state=checked]:bg-info",
    members: ["INVITED", "invited"],
  },
  {
    id: "confirmed",
    name: "Confirmed",
    color: "success",
    icon: CheckCircle,
    control: "data-[state=checked]:border-success data-[state=checked]:bg-success",
    members: ["CONFIRMED", "accepted"],
  },
  {
    id: "reschedule_requested",
    name: "Reschedule requested",
    color: "warning",
    icon: Warning,
    control: "data-[state=checked]:border-warning data-[state=checked]:bg-warning",
    members: ["RESCHEDULE_REQUESTED", "reschedule_requested"],
  },
  {
    id: "completed",
    name: "Completed",
    color: "brand",
    icon: CheckCircle,
    control: "data-[state=checked]:border-brand data-[state=checked]:bg-brand",
    members: ["COMPLETED", "REVIEWED", "PAID", "completed"],
  },
  {
    id: "needs_payment",
    name: "Needs payment",
    color: "warning",
    icon: Warning,
    control: "data-[state=checked]:border-warning data-[state=checked]:bg-warning",
    members: ["PENDING_PAYMENT"],
  },
  {
    id: "processing",
    name: "Payment processing",
    color: "gray",
    icon: InfoOutline,
    control: "data-[state=checked]:border-gray data-[state=checked]:bg-gray",
    members: ["PAYMENT_SUBMITTED"],
  },
  {
    id: "payment_issue",
    name: "Payment failed",
    color: "error",
    icon: Warning,
    control: "data-[state=checked]:border-error data-[state=checked]:bg-error",
    members: ["PAYMENT_FAILED", "PAYMENT_ERROR"],
  },
  {
    id: "cancelled",
    name: "Cancelled",
    color: "medium-gray",
    icon: InfoOutline,
    control: "data-[state=checked]:border-medium-gray data-[state=checked]:bg-medium-gray",
    members: ["CANCELLED"],
  },
  {
    id: "declined",
    name: "Declined",
    color: "error",
    icon: InfoOutline,
    control: "data-[state=checked]:border-error data-[state=checked]:bg-error",
    members: ["DECLINED", "declined"],
  },
] as const satisfies ReadonlyArray<StatusBucket>;

export { STATUS_BUCKETS };
export type StatusBucketId = (typeof STATUS_BUCKETS)[number]["id"];

const bucketFor = (status: string) =>
  STATUS_BUCKETS.find((bucket) => (bucket.members as ReadonlyArray<string>).includes(status));

export const bucketOf = (status: string): StatusBucketId | undefined => bucketFor(status)?.id;

export const statusBucketStyle = (status: string) => {
  const bucket = bucketFor(status);
  if (!bucket) return;
  const s = STATUS_STYLES[bucket.color];
  return {
    id: bucket.id,
    name: bucket.name,
    icon: bucket.icon,
    pill: `${s.bg} ${s.text}`,
    bg: s.bg,
    text: s.text,
    card: s.card,
    chip: s.chip,
    accent: s.accent,
  };
};
